import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { useCompany } from './context/CompanyContext';
import { usePhase76Auth } from './lib/auth';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import {
  AuthProvider,
  useAuth,
  LoginPage,
  SignUpPage,
  ProtectedRoute,
  RequirePermission,
  // Phase 75 — multi-tenant module access guard with auto-switch fallback.
  ModuleGuard,
  UpdatePasswordPage,
} from './features/auth';
import { CompanyProvider } from './context/CompanyContext';
// Phase 76.2.A — additive RBAC context, sits BESIDE CompanyProvider (never replaces).
// Hydrates get_my_companies / get_my_permissions_for_company / is_super_admin
// and caches per-company permission sets for the new evolutionary RBAC surface.
import { Phase76AuthProvider, hasWorkspaceSelection } from './lib/auth';
import { B2BUserProvider } from './pages/b2b/B2BUserContext';
import { ToastContainer }  from './components/ui/Toast';
import { Layout, CashFlowLayout, InventoryLayout, HRLayout, InvoiceIntakeLayout, InterfacesLayout } from './components/layout';
// Phase 75 — graceful render-error fallback for module shells.
import { ModuleErrorBoundary } from './components/ModuleErrorBoundary';
import {
  AxelioLandingPage,
  DashboardPage,
  SettingsPage,
  OnboardingPage,
  WorkspacesPage,
  CompanySettingsPage,
  TeamSettingsPage,
  CashFlowDashboardPage,
  CashflowTablesPage,
  CashflowSettingsPage,
  HRDashboardPage,
  ActionLogPage,
  RecruitmentPage,
  PayrollPage,
  ShiftManagementPage,
  InviteLandingPage,
  JoinPage,
  SuperAdminDashboard,
  InventoryDashboardPage,
  InventorySettingsPage,
  InventoryProductsPage,
  InventoryMovementsPage,
  ConsignmentPage,
  DocumentGenerationPage,
  ImportSyncPage,
  StockCountPage,
  ClientLedgerPage,
  IntegrationsHubPage,
  ShopifyIntegrationSettings,
  RivhitIntegrationSettings,
  TranzilaIntegrationSettings,
  ComingSoonIntegrationSettings,
  PrivacyPolicyPage,
  ShopifyCallbackPage,
  B2BLoginPage,
  B2BRoute,
  B2BLayout,
  B2BCatalogPage,
  B2BDashboardPage,
  B2BOrdersPage,
  B2BReturnPage,
  B2BOrderRMAPage,
  B2BLifoReturnPage,
  B2BReturnCatalogPage,
  B2BAgentPage,
  B2BPricelistsPage,
  B2BCustomersPage,
  B2BConsignmentReportPage,
  B2BAccountPage,
  B2BSettingsPage,
  B2BTermsPage,
  B2BInventoryBalancesPage,
  B2BMyStockPage,
  B2BInventoryMovementsPage,
  AIReportsPage,
  SkillsStorePage,
  EmployeeUserProvider,
  EmployeeRoute,
  EmployeeLoginPage,
  EmployeeLayout,
  EmployeeProfilePage,
  EmployeeEmploymentsPage,
  EmployeeApprovalsPage,
  EmployeeInvitePage,
  InvoiceIntakeDashboardPage,
  InvoiceUploadPage,
  InvoiceReviewPage,
  InvoiceHistoryPage,
  InvoiceDiscrepanciesPage,
  InvoiceIntakeSettingsPage,
  InterfacesSetupPage,
  InterfacesFlowsPage,
  InterfacesActivityLogPage,
  InterfacesPendingOrdersPage,
} from './pages';

// ─── App ──────────────────────────────────────────────────────────────────────

// ─── Company Context ↔ Phase76 Sync ─────────────────────────────────────────
// CompanyContext and Phase76AuthContext each manage their own activeCompanyId.
// When the user changes company via CompanySelector (or any other path that
// calls CompanyContext.setActiveCompanyId), Phase76 needs to know immediately
// so useHasModule / hasModuleLocal re-evaluates against the correct company's
// subscription set. This bridge component lives inside both providers.
function Phase76CompanySync() {
  const { activeCompanyId } = useCompany();
  const { setActiveCompanyId: setPhase76CompanyId } = usePhase76Auth();

  useEffect(() => {
    if (activeCompanyId) {
      setPhase76CompanyId(activeCompanyId);
    }
  }, [activeCompanyId, setPhase76CompanyId]);

  return null;
}

// ─── Phase 45: Password-recovery watcher ────────────────────────────────────
// Must live INSIDE <BrowserRouter> (needs useNavigate) but INSIDE <AuthProvider>
// (needs useAuth). Navigates to /update-password the moment the listener fires.
function PasswordRecoveryWatcher() {
  const { isPasswordRecovery } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isPasswordRecovery) {
      navigate('/update-password', { replace: true });
    }
  }, [isPasswordRecovery, navigate]);

  return null; // renders nothing
}

// ─── Phase 77: PostLoginGate ─────────────────────────────────────────────────
//
// Intercepts the base post-login routing path. A FRESH authenticated session
// (one that has not yet flipped the per-tab `axelio_session_explicit_company`
// flag in sessionStorage) is force-redirected to /workspaces so the user
// EXPLICITLY picks their active company target from their authorized list,
// rather than auto-hydrating into whichever company was cached in
// localStorage from a previous session.
//
// Once the user picks a company on /workspaces (which calls
// `setActiveCompanyId`, which calls `markWorkspaceSelected`), the flag is
// set and `/app/*` becomes reachable for the rest of this tab's session.
// Internal navigations and page reloads within an established session
// keep the flag set, so the localStorage cache continues to serve its
// original purpose for cross-tab sync and reloads.
//
// The Workspaces route itself is intentionally NOT wrapped — otherwise the
// gate would loop the user. Same for /onboarding (where the user is
// landing because they have NO company yet) and /update-password.
function PostLoginGate({ children }: { children: ReactNode }) {
  if (!hasWorkspaceSelection()) {
    return <Navigate to="/workspaces" replace />;
  }
  return <>{children}</>;
}

// ─── App ──────────────────────────────────────────────────────────────────────

function IntegrationSettingsRedirect() {
  const { provider } = useParams<{ provider: string }>();
  return <Navigate to={`/app/settings/integrations/${encodeURIComponent(provider ?? '')}`} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <CompanyProvider>
            <Phase76AuthProvider>
            <ToastContainer />
            {/* Keeps Phase76 activeCompanyId in sync with CompanyContext */}
            <Phase76CompanySync />
            {/* Phase 45: watches for PASSWORD_RECOVERY and redirects */}
            <PasswordRecoveryWatcher />
            <Routes>

              {/* ── Public Marketing Landing Page ────────────────────────── */}
              <Route path="/" element={<AxelioLandingPage />} />

              {/* ── Public routes ──────────────────────────────────────────── */}
              <Route path="/login"           element={<LoginPage />} />
              <Route path="/signup"          element={<SignUpPage />} />
              <Route path="/invite"          element={<InviteLandingPage />} />
              {/* /join?company=<token>  — query-string invite link */}
              <Route path="/join"            element={<InviteLandingPage />} />
              {/* /join/<token>          — URL-segment invite link */}
              <Route path="/join/:inviteToken" element={<JoinPage />} />
              {/* Phase 45: password-recovery landing — PUBLIC (no ProtectedRoute) */}
              <Route path="/update-password" element={<UpdatePasswordPage />} />

              {/* ── 403 Forbidden ─────────────────────────────────────────── */}
              <Route path="/403"    element={<ForbiddenPage />} />
              <Route path="/privacy"           element={<PrivacyPolicyPage />} />
              <Route path="/shopify-callback"  element={<ShopifyCallbackPage />} />

              {/* ── Onboarding (authenticated, no company check) ──────────── */}
              <Route
                path="/onboarding"
                element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>}
              />


              {/* ── Global settings redirects (bare paths → /app/* equivalents) */}
              <Route path="/settings"                  element={<Navigate to="/app/settings"                  replace />} />
              <Route path="/settings/company"         element={<Navigate to="/app/settings/company"         replace />} />
              <Route path="/settings/team"            element={<Navigate to="/app/settings/team"            replace />} />
              <Route path="/settings/integrations"            element={<Navigate to="/app/settings/integrations"            replace />} />
              <Route path="/settings/integrations/shopify"   element={<Navigate to="/app/settings/integrations/shopify"   replace />} />
              <Route path="/settings/integrations/rivhit"    element={<Navigate to="/app/settings/integrations/rivhit"    replace />} />
              <Route path="/settings/integrations/:provider" element={<IntegrationSettingsRedirect />} />

              {/* ── Workspaces ────────────────────────────────────────────── */}
              {/*
                Accessible by any authenticated user regardless of module.
                Uses the generic Layout (Navbar + full Sidebar) so users can
                navigate back to any module after switching workspace.
              */}
              <Route
                path="/workspaces"
                element={<ProtectedRoute><Layout /></ProtectedRoute>}
              >
                <Route index element={<WorkspacesPage />} />
              </Route>

              {/* ── Protected app shell ───────────────────────────────────── */}
              {/*
                Phase 77: <PostLoginGate> intercepts the base post-login
                routing path. A fresh authenticated session (no per-tab
                workspace-selection flag yet) is force-redirected to
                /workspaces so the user EXPLICITLY chooses their active
                company. Once a company is picked, the flag is set and
                /app/* becomes reachable for the rest of this session.
              */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <PostLoginGate>
                      <Layout />
                    </PostLoginGate>
                  </ProtectedRoute>
                }
              >
                {/* Dashboard — all authenticated users */}
                <Route index element={<DashboardPage />} />
                <Route path="dashboard" element={<DashboardPage />} />

                {/* Placeholder routes */}
                <Route path="transactions" element={<PlaceholderPage title="Transactions" />} />
                <Route path="accounts"     element={<PlaceholderPage title="Accounts" />} />

                {/* ── Settings ──────────────────────────────────────────── */}
                <Route path="settings"         element={<SettingsPage />} />
                <Route path="settings/company" element={<CompanySettingsPage />} />
                <Route
                  path="settings/team"
                  element={
                    <RequirePermission requireCompanyAdmin>
                      <TeamSettingsPage />
                    </RequirePermission>
                  }
                />
                <Route path="settings/integrations"            element={<IntegrationsHubPage />} />
                <Route path="settings/integrations/shopify"   element={<ShopifyIntegrationSettings />} />
                <Route path="settings/integrations/rivhit"    element={<RivhitIntegrationSettings />} />
                <Route path="settings/integrations/tranzila"  element={<TranzilaIntegrationSettings />} />
                <Route path="settings/integrations/:provider" element={<ComingSoonIntegrationSettings />} />

                {/* ── AI Reports ────────────────────────────────────────── */}
                <Route path="reports/ai" element={<AIReportsPage />} />

                {/* ── Skills Store ──────────────────────────────────────── */}
                <Route path="skills" element={<SkillsStorePage />} />
                <Route path="skills/history" element={<SkillsStorePage />} />

                {/* ── Super-admin backoffice (plan_tier: admin only) ──────── */}
                <Route
                  path="admin"
                  element={
                    <RequirePermission minPlan="admin" redirectTo="/403">
                      <SuperAdminDashboard />
                    </RequirePermission>
                  }
                />
              </Route>

              {/* ── Cash Flow Module (Dedicated Layout) ───────────────── */}
              {/*
                /app/cashflow/*  → CashFlowLayout (streamlined sidebar)

                Phase 75 wraps this shell in three layers, in order:
                  1. <ProtectedRoute>      — auth gate (must be signed in)
                  2. <ModuleErrorBoundary> — graceful render-error fallback
                                              that keeps the user inside the
                                              app shell instead of redirecting
                                              to '/' on uncaught exceptions.
                  3. <ModuleGuard module="cashflow">
                                            — multi-tenant access guard that
                                              (a) auto-switches to another
                                              authorized company when the
                                              active one is not licensed,
                                              and (b) falls back to the
                                              branded UnauthorizedModulePage
                                              when no other company helps.
                                              Replaces the prior <Require-
                                              Permission module=...> at this
                                              level — leaf-level level guards
                                              (editor / admin) still apply
                                              inside the shell.
              */}
              <Route
                path="/app/cashflow"
                element={
                  <ProtectedRoute>
                    <PostLoginGate>
                      <ModuleErrorBoundary
                        moduleLabel="תזרים מזומנים"
                        moduleHomePath="/app/cashflow"
                      >
                        <ModuleGuard module="cashflow">
                          <CashFlowLayout />
                        </ModuleGuard>
                      </ModuleErrorBoundary>
                    </PostLoginGate>
                  </ProtectedRoute>
                }
              >
                {/* Cash Flow Dashboard — landing page for this module */}
                <Route index element={<CashFlowDashboardPage />} />
                
                {/* Cash Flow Tables */}
                <Route path="tables" element={<CashflowTablesPage />} />
                
                {/* Cash Flow Settings */}
                <Route
                  path="settings"
                  element={
                    <RequirePermission module="cashflow" level="editor">
                      <CashflowSettingsPage />
                    </RequirePermission>
                  }
                />
              </Route>

              {/* ── Inventory Module (Dedicated Layout) ───────────────── */}
              {/*
                /app/inventory/*  → InventoryLayout (streamlined sidebar)

                Phase 75: wrapped in <ModuleErrorBoundary> + <ModuleGuard>
                so cross-company access leaks (Eli's scenario where the
                active company isn't licensed for inventory but another of
                the user's companies is) auto-switch instead of redirecting
                the user out to the public landing page.
              */}
              <Route
                path="/app/inventory"
                element={
                  <ProtectedRoute>
                    <PostLoginGate>
                      <ModuleErrorBoundary
                        moduleLabel="ניהול מלאי"
                        moduleHomePath="/app/inventory"
                      >
                        <ModuleGuard module="inventory">
                          <InventoryLayout />
                        </ModuleGuard>
                      </ModuleErrorBoundary>
                    </PostLoginGate>
                  </ProtectedRoute>
                }
              >
                {/* Inventory Dashboard — landing page for this module */}
                <Route index element={<InventoryDashboardPage />} />
                
                {/* Inventory Movements (renamed: ניהול הזמנות) */}
                <Route path="movements" element={<InventoryMovementsPage />} />

                {/* Consignment Management — placeholder (ניהול קונסגנציה) */}
                <Route path="consignment" element={<ConsignmentPage />} />

                {/* Inventory Stock (renamed: מלאי ומוצרים) */}
                <Route path="stock" element={<InventoryProductsPage />} />

                {/* Stock Count (ספירת מלאי) */}
                <Route path="stock-count" element={<StockCountPage />} />
                
                {/* Document Generation Hub */}
                <Route
                  path="documents/new"
                  element={
                    <RequirePermission module="inventory" level="editor">
                      <DocumentGenerationPage />
                    </RequirePermission>
                  }
                />

                {/* Universal Product Import Hub (Phase 49) */}
                <Route
                  path="import"
                  element={<ImportSyncPage />}
                />

                {/* Phase 96: Client ledger */}
                <Route
                  path="clients/:clientId/ledger"
                  element={<ClientLedgerPage />}
                />

                {/* Inventory Settings */}
                <Route
                  path="settings"
                  element={
                    <RequirePermission module="inventory" level="editor">
                      <InventorySettingsPage />
                    </RequirePermission>
                  }
                />
              </Route>

              {/* ── HR/Employees Module (Dedicated Layout) ────────────── */}
              {/*
                /app/hr/*  → HRLayout (streamlined sidebar)

                Phase 75: wrapped in <ModuleErrorBoundary> + <ModuleGuard>
                — same multi-tenant access pattern as cashflow / inventory.
              */}
              <Route
                path="/app/hr"
                element={
                  <ProtectedRoute>
                    <PostLoginGate>
                      <ModuleErrorBoundary
                        moduleLabel="ניהול עובדים"
                        moduleHomePath="/app/hr"
                      >
                        <ModuleGuard module="hr">
                          <HRLayout />
                        </ModuleGuard>
                      </ModuleErrorBoundary>
                    </PostLoginGate>
                  </ProtectedRoute>
                }
              >
                <Route index element={<HRDashboardPage />} />
                <Route path="recruitment" element={<RecruitmentPage />} />
                <Route path="payroll" element={<PayrollPage />} />
                <Route path="shifts" element={<ShiftManagementPage />} />
                <Route path="action-log" element={<ActionLogPage />} />
              </Route>

              {/* ── Invoice Intake Module (Dedicated Layout) ────────── */}
              <Route
                path="/app/invoice-intake"
                element={
                  <ProtectedRoute>
                    <PostLoginGate>
                      <ModuleErrorBoundary
                        moduleLabel="קליטת חשבוניות"
                        moduleHomePath="/app/invoice-intake"
                      >
                        <ModuleGuard module="invoice_intake">
                          <InvoiceIntakeLayout />
                        </ModuleGuard>
                      </ModuleErrorBoundary>
                    </PostLoginGate>
                  </ProtectedRoute>
                }
              >
                <Route index element={<InvoiceIntakeDashboardPage />} />
                <Route path="upload" element={<InvoiceUploadPage />} />
                <Route path="review/:invoiceId" element={<InvoiceReviewPage />} />
                <Route path="history" element={<InvoiceHistoryPage />} />
                <Route path="discrepancies" element={<InvoiceDiscrepanciesPage />} />
                <Route path="settings" element={<InvoiceIntakeSettingsPage />} />
              </Route>

              {/* ── Interfaces Module (Dedicated Layout) ──────────────── */}
              <Route
                path="/app/interfaces"
                element={
                  <ProtectedRoute>
                    <PostLoginGate>
                      <ModuleErrorBoundary
                        moduleLabel="ממשקים"
                        moduleHomePath="/app/interfaces"
                      >
                        <ModuleGuard module="interfaces">
                          <InterfacesLayout />
                        </ModuleGuard>
                      </ModuleErrorBoundary>
                    </PostLoginGate>
                  </ProtectedRoute>
                }
              >
                <Route index element={<InterfacesSetupPage />} />
                <Route path="flows" element={<InterfacesFlowsPage />} />
                <Route path="pending" element={<InterfacesPendingOrdersPage />} />
                <Route path="log" element={<InterfacesActivityLogPage />} />
              </Route>

              {/* ── B2B Marketplace (PWA) ─────────────────────────────── */}
              {/*
                /b2b          → B2BLoginPage (public; B2BRoute redirects
                                 already-authed users to /b2b/catalog)
                /b2b/*        → nested under B2BLayout (cart provider +
                                 header + bottom nav + cart drawer)

                B2BLayout wraps catalog, dashboard, orders and agent.
                B2BRoute handles the loading spinner and
                "already-authed → /b2b/catalog" redirect.
              */}

              {/*
                B2BUserProvider is hoisted here — above BOTH the login route
                and the authenticated layout route — so that one single context
                instance persists across the navigate('/b2b/agent') call that
                follows a successful login.

                Without this, each <B2BRoute> mounts its own <B2BUserProvider>,
                so seedB2BUser() writes into Provider #1 (login route) and then
                navigate() destroys it and creates Provider #2 (layout route)
                which starts with session = null — causing "פרטי הסוכן לא נמצאו".
              */}
              {/* ─── Phase 101: Employee Portal ─────────────────────────────
                /employee          → EmployeeLoginPage (public)
                /employee/invite/* → Invite landing (public)
                /employee/portal/* → Authenticated employee pages
              */}
              <Route element={<EmployeeUserProvider><EmployeeRoute /></EmployeeUserProvider>}>
                <Route path="/employee" element={<EmployeeLoginPage />} />
                <Route path="/employee/invite/:token" element={<EmployeeInvitePage />} />
                <Route path="/employee/portal" element={<EmployeeLayout />}>
                  <Route index element={<EmployeeProfilePage />} />
                  <Route path="employments" element={<EmployeeEmploymentsPage />} />
                  <Route path="approvals" element={<EmployeeApprovalsPage />} />
                </Route>
              </Route>

              <Route element={<B2BUserProvider><B2BRoute /></B2BUserProvider>}>

                {/* Login page — intentionally outside B2BLayout */}
                <Route
                  path="/b2b"
                  element={<B2BLoginPage />}
                />

                {/* Authenticated B2B pages — share the layout shell */}
                <Route
                  path="/b2b"
                  element={<B2BLayout />}
                >
                  <Route path="catalog"    element={<B2BCatalogPage />} />
                  <Route path="dashboard"  element={<B2BDashboardPage />} />
                  <Route path="orders"     element={<B2BOrdersPage />} />
                  {/* Phase 66: Order-Based RMA — opened from a specific order */}
                  <Route path="orders/:orderId/rma" element={<B2BOrderRMAPage />} />
                  {/* Phase 135: Catalog-style return document page */}
                  <Route path="returns"    element={<B2BReturnCatalogPage />} />
                  {/* Phase 67: Legacy LIFO return page — kept at /returns/lifo */}
                  <Route path="returns/lifo" element={<B2BLifoReturnPage />} />
                  {/* Phase 62: legacy blind-search RMA — kept for back-office
                      use only, accessible via direct URL.  Replaced in main
                      navigation by the Phase 67 LIFO flow above. */}
                  <Route path="returns/legacy" element={<B2BReturnPage />} />
                  <Route path="agent"      element={<B2BAgentPage />} />
                  {/* Phase 51: Agent-only price list management */}
                  <Route path="pricelists" element={<B2BPricelistsPage />} />
                  {/* Phase 52: Agent-only customer management */}
                  <Route path="customers" element={<B2BCustomersPage />} />
                  {/* Phase 90: Consignment sale report */}
                  <Route path="consignment-report" element={<B2BConsignmentReportPage />} />
                  {/* Phase 96: Client account ledger */}
                  <Route path="account/:clientId" element={<B2BAccountPage />} />
                  <Route path="settings" element={<B2BSettingsPage />} />
                  <Route path="terms" element={<B2BTermsPage />} />
                  {/* Phase 126: Agent inventory reports */}
                  <Route path="inventory" element={<B2BInventoryBalancesPage />} />
                  <Route path="movements" element={<B2BInventoryMovementsPage />} />
                  <Route path="my-stock" element={<B2BMyStockPage />} />
                </Route>

              </Route>

              {/* Phase 75: 404 → smart fallback.
                  Previously, ALL un-matched paths dropped the user onto the
                  public marketing landing page ('/') — even when they were
                  signed in and simply mistyped a URL or arrived after an
                  internal navigation failure.  We now route un-matched paths
                  through <SafeNotFoundRedirect/> which sends authenticated
                  users back to /app (keeping them inside the shell) and
                  anonymous users to '/'. */}
              <Route path="*" element={<SafeNotFoundRedirect />} />

            </Routes>
            </Phase76AuthProvider>
          </CompanyProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// ─── Placeholder / utility pages ─────────────────────────────────────────────

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">{title}</h1>
        <p className="text-gray-600">This page is under construction</p>
      </div>
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50 text-gray-500">
      <p className="text-6xl font-black text-gray-200">403</p>
      <p className="text-lg font-semibold text-gray-700">אין לך הרשאה לגשת לדף זה</p>
      <p className="text-sm text-gray-400">
        הרשאות חסרות — פנה למנהל החשבון שלך.
      </p>
      <a href="/app" className="mt-4 px-6 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">
        חזרה לדף הבית
      </a>
    </div>
  );
}

// ─── Phase 75: SafeNotFoundRedirect ──────────────────────────────────────────
//
// Replaces the legacy `<Navigate to="/" replace />` catch-all that abruptly
// dropped EVERY un-matched URL onto the public marketing landing page —
// including ones reached by signed-in users via typo, stale link, or an
// internal navigation failure cascading out of a module crash.
//
// Behaviour matrix:
//   • Auth state still loading  → render a neutral spinner (no decision yet,
//     prevents flashing the landing page during the first paint).
//   • Authenticated user         → redirect to /app (in-shell home).
//   • Anonymous user             → redirect to '/' (public landing — same as
//                                  the previous behaviour for un-authed paths).
//
function SafeNotFoundRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-400">
        טוען…
      </div>
    );
  }

  return <Navigate to={user ? '/app' : '/'} replace />;
}

export default App;
